import React, { useState, useEffect } from 'react';
import {
  Globe, Landmark, TrendingUp, TrendingDown, Minus, RefreshCw, X,
  Calendar, Info, ArrowUpRight, Activity, Zap, ShieldAlert
} from 'lucide-react';
import { MacroIndicators, PolicyRatesData, CentralBankRateItem } from '../types';
import { fetchPolicyRates } from '../services/api';

interface LiveMarketIndicatorsModalProps {
  isOpen: boolean;
  onClose: () => void;
  indicators?: MacroIndicators | null;
  language?: string;
  marketMode?: 'US' | 'KR';
  onForceRefresh?: () => Promise<void>;
}

export function LiveMarketIndicatorsModal({
  isOpen,
  onClose,
  indicators,
  language = 'ko',
  marketMode = 'US',
  onForceRefresh
}: LiveMarketIndicatorsModalProps) {
  const lang = language || 'ko';
  const [activeCategory, setActiveCategory] = useState<'ALL' | 'RATES' | 'INDICES' | 'FX_BONDS' | 'COMMODITIES'>('ALL');
  const [isRefreshing, setIsRefreshing] = useState<boolean>(false);
  const [policyRates, setPolicyRates] = useState<PolicyRatesData | null>(indicators?.policyRates || null);

  useEffect(() => {
    if (indicators?.policyRates) {
      setPolicyRates(indicators.policyRates);
    } else if (isOpen && !policyRates) {
      fetchPolicyRates().then(data => {
        if (data) setPolicyRates(data);
      });
    }
  }, [isOpen, indicators?.policyRates]);

  if (!isOpen) return null;

  const handleRefresh = async () => {
    setIsRefreshing(true);
    try {
      if (onForceRefresh) {
        await onForceRefresh();
      }
      const updatedRates = await fetchPolicyRates();
      if (updatedRates) setPolicyRates(updatedRates);
    } catch (e) {
      console.error('Refresh error in LiveMarketIndicatorsModal:', e);
    } finally {
      setIsRefreshing(false);
    }
  };

  const rawFed = policyRates?.fed;
  const fed: CentralBankRateItem = (rawFed && (rawFed.targetRateUpper || 0) >= 4.00) ? rawFed : {
    id: 'FED',
    bankName: 'Federal Reserve (FOMC)',
    rateType: 'RANGE' as const,
    display: '3.75% ~ 4.00%',
    targetRateLower: 3.75,
    targetRateUpper: 4.00,
    changeBp: 25,
    action: 'HIKE' as const,
    decisionDate: '2026-09-16',
    effectiveDate: '2026-09-17',
    nextMeetingDate: '2026-11-04',
    statementSummary: 'FOMC 기준금리 25bp 인상 의결 (목표범위 3.75% ~ 4.00%)',
    source: 'FRED:DFEDTARU,DFEDTARL'
  };

  const bok = policyRates?.bok || {
    id: 'BOK',
    bankName: 'Bank of Korea (한국은행)',
    rateType: 'SINGLE' as const,
    display: '3.50%',
    targetRateSingle: 3.50,
    changeBp: 0,
    action: 'HOLD' as const,
    decisionDate: '2026-08-27',
    effectiveDate: '2026-08-27',
    nextMeetingDate: '2026-10-22',
    statementSummary: '금통위 기준금리 3.50% 동결 의결',
    source: 'BOK:ECOS'
  };

  const renderActionBadge = (item: CentralBankRateItem) => {
    if (item.action === 'HIKE' || item.changeBp > 0) {
      return (
        <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[11px] font-black bg-rose-500/15 text-rose-400 border border-rose-500/30">
          <TrendingUp className="w-3.5 h-3.5" />
          {lang === 'ko' ? `+${item.changeBp}bp 인상` : `+${item.changeBp}bp Hike`}
        </span>
      );
    }
    if (item.action === 'CUT' || item.changeBp < 0) {
      return (
        <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[11px] font-black bg-emerald-500/15 text-emerald-400 border border-emerald-500/30">
          <TrendingDown className="w-3.5 h-3.5" />
          {lang === 'ko' ? `${item.changeBp}bp 인하` : `${item.changeBp}bp Cut`}
        </span>
      );
    }
    return (
      <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[11px] font-black bg-slate-500/15 text-slate-300 border border-slate-500/30">
        <Minus className="w-3.5 h-3.5" />
        {lang === 'ko' ? '동결 (0bp)' : 'Hold (0bp)'}
      </span>
    );
  };

  // 실시간 지표 목록 정규화
  const marketItems = [
    // 1. 주요 지수
    { category: 'INDICES', label: 'S&P 500', val: indicators?.sp500?.value, chg: indicators?.sp500?.change, unit: '', digits: 2 },
    { category: 'INDICES', label: 'Nasdaq', val: indicators?.nasdaq?.value, chg: indicators?.nasdaq?.change, unit: '', digits: 2 },
    { category: 'INDICES', label: 'SOX (반도체)', val: indicators?.sox?.value, chg: indicators?.sox?.change, unit: '', digits: 2 },
    { category: 'INDICES', label: 'KOSPI', val: indicators?.kospi?.value, chg: indicators?.kospi?.change, unit: '', digits: 2 },
    { category: 'INDICES', label: 'KOSDAQ', val: indicators?.kosdaq?.value, chg: indicators?.kosdaq?.change, unit: '', digits: 2 },
    { category: 'INDICES', label: '다우존스 (DJIA)', val: indicators?.djia?.value, chg: indicators?.djia?.change, unit: '', digits: 2 },
    { category: 'INDICES', label: '러셀 2000', val: indicators?.russell2000?.value, chg: indicators?.russell2000?.change, unit: '', digits: 2 },

    // 2. 변동성 & 센티멘트
    { category: 'INDICES', label: 'VIX 변동성', val: indicators?.vix?.value, chg: indicators?.vix?.change, unit: '', digits: 2, isReverse: true },
    { category: 'INDICES', label: 'VKOSPI', val: indicators?.vkospi?.value, chg: indicators?.vkospi?.change, unit: '', digits: 2, isReverse: true },
    { category: 'INDICES', label: 'Fear & Greed Index', val: indicators?.fearGreed?.value, chg: indicators?.fearGreed?.change, unit: 'p', digits: 0, isScore: true },
    { category: 'INDICES', label: 'KOSPI FGI', val: indicators?.kospiFgi?.value, chg: indicators?.kospiFgi?.change, unit: 'p', digits: 0, isScore: true },

    // 3. 외환 및 채권
    { category: 'FX_BONDS', label: 'USD/KRW 환율', val: indicators?.usdkrw?.value, chg: indicators?.usdkrw?.change, unit: '원', digits: 1, isReverse: true },
    { category: 'FX_BONDS', label: 'DXY 달러 인덱스', val: indicators?.dxy?.value, chg: indicators?.dxy?.change, unit: '', digits: 2, isReverse: true },
    { category: 'FX_BONDS', label: '미 10년물 국채금리', val: indicators?.government?.treasury10Y?.value, chg: indicators?.government?.treasury10Y?.change, unit: '%', digits: 2 },
    { category: 'FX_BONDS', label: '미 2년물 국채금리', val: indicators?.government?.treasury2Y?.value, chg: indicators?.government?.treasury2Y?.change, unit: '%', digits: 2 },
    { category: 'FX_BONDS', label: '10Y-2Y 장단기 스프레드', val: indicators?.yieldSpread?.value, chg: indicators?.yieldSpread?.change, unit: '%', digits: 2 },
    { category: 'FX_BONDS', label: '하이일드 스프레드', val: indicators?.government?.highYieldSpread?.value, chg: indicators?.government?.highYieldSpread?.change, unit: '%', digits: 2, isReverse: true },

    // 4. 원자재 & 글로벌 대안지표
    { category: 'COMMODITIES', label: '비트코인 (BTC)', val: indicators?.bitcoin?.value, chg: indicators?.bitcoin?.change, unit: '$', digits: 0 },
    { category: 'COMMODITIES', label: '금 (Gold)', val: indicators?.gold?.value, chg: indicators?.gold?.change, unit: '$', digits: 1 },
    { category: 'COMMODITIES', label: '은 (Silver)', val: indicators?.silver?.value, chg: indicators?.silver?.change, unit: '$', digits: 2 },
    { category: 'COMMODITIES', label: 'WTI 원유', val: indicators?.oil?.value, chg: indicators?.oil?.change, unit: '$', digits: 2 },
    { category: 'COMMODITIES', label: '구리 (Copper)', val: indicators?.copper?.value, chg: indicators?.copper?.change, unit: '$', digits: 2 },
    { category: 'COMMODITIES', label: '천연가스', val: indicators?.naturalGas?.value, chg: indicators?.naturalGas?.change, unit: '$', digits: 2 },
  ];

  const filteredMarketItems = activeCategory === 'ALL'
    ? marketItems
    : marketItems.filter(i => i.category === activeCategory);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm animate-fade-in">
      <div className="bg-slate-900 border border-white/15 rounded-3xl w-full max-w-5xl max-h-[92vh] flex flex-col shadow-2xl overflow-hidden">
        
        {/* Modal Header */}
        <div className="p-6 border-b border-white/10 flex items-center justify-between bg-gradient-to-r from-indigo-500/10 via-transparent to-purple-500/10">
          <div className="flex items-center gap-3">
            <div className="p-2.5 rounded-2xl bg-indigo-500/20 border border-indigo-500/30 text-indigo-400">
              <Globe className="w-6 h-6" />
            </div>
            <div>
              <div className="flex items-center gap-2">
                <h3 className="text-xl font-black text-white tracking-tight">
                  {lang === 'ko' ? '시장 지표 (실시간) & 주요국 정책금리' : 'Live Market Indicators & Policy Rates'}
                </h3>
                <span className="px-2.5 py-0.5 text-[10px] font-black uppercase rounded-full bg-emerald-500/20 text-emerald-400 border border-emerald-500/30">
                  LIVE SSOT
                </span>
              </div>
              <p className="text-xs text-white/50 mt-0.5">
                {lang === 'ko'
                  ? 'FOMC 및 한국은행의 공식 발표 기준금리와 실시간 글로벌 시장 지표를 통합 모니터링합니다.'
                  : 'Real-time central bank policy decisions (FOMC & BOK) and global multi-asset market indicators.'}
              </p>
            </div>
          </div>

          <div className="flex items-center gap-2">
            <button
              onClick={handleRefresh}
              disabled={isRefreshing}
              className="px-3.5 py-2 rounded-xl bg-white/5 hover:bg-white/10 border border-white/10 text-white/80 hover:text-white text-xs font-bold flex items-center gap-1.5 transition-all cursor-pointer disabled:opacity-50"
              title={lang === 'ko' ? '최신 데이터 즉시 새로고침' : 'Force Refresh Data'}
            >
              <RefreshCw className={`w-3.5 h-3.5 ${isRefreshing ? 'animate-spin text-indigo-400' : ''}`} />
              <span>{isRefreshing ? (lang === 'ko' ? '갱신 중...' : 'Refreshing...') : (lang === 'ko' ? '새로고침' : 'Refresh')}</span>
            </button>
            <button
              onClick={onClose}
              className="p-2 rounded-xl bg-white/5 hover:bg-white/10 border border-white/10 text-white/60 hover:text-white transition-all cursor-pointer"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Modal Scrollable Body */}
        <div className="flex-1 overflow-y-auto custom-scrollbar p-6 space-y-6">
          
          {/* SECTION 1: 중앙은행 공식 기준금리 결정 (하이라이트 카드) */}
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Landmark className="w-4 h-4 text-indigo-400" />
                <h4 className="text-xs font-black uppercase tracking-wider text-white/80">
                  {lang === 'ko' ? '중앙은행 공식 정책금리 의결 현황' : 'Official Central Bank Policy Rate Decisions'}
                </h4>
              </div>
              <span className="text-[10px] text-white/40 font-mono">
                {lang === 'ko' ? '발표 즉시 공식 반영' : 'Instant Official Reflection'}
              </span>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* FOMC 연준 카드 */}
              <div className="bg-gradient-to-br from-indigo-500/10 via-slate-800/60 to-slate-900 border border-indigo-500/30 rounded-2xl p-5 relative overflow-hidden">
                <div className="absolute top-0 right-0 w-32 h-32 bg-indigo-500/5 rounded-full blur-2xl pointer-events-none" />
                <div className="flex items-start justify-between relative z-10">
                  <div className="flex items-center gap-2.5">
                    <span className="text-2xl">🇺🇸</span>
                    <div>
                      <h5 className="text-sm font-black text-white">{fed.bankName}</h5>
                      <span className="text-[10px] text-white/40 font-mono">FRED ID: DFEDTARU / DFEDTARL</span>
                    </div>
                  </div>
                  {renderActionBadge(fed)}
                </div>

                <div className="mt-4 pt-3 border-t border-white/5 flex items-baseline justify-between relative z-10">
                  <div>
                    <span className="text-[10px] text-indigo-300 font-bold uppercase tracking-wider block">
                      {lang === 'ko' ? '연방기금 목표금리 범위 (Target Range)' : 'Federal Funds Target Range'}
                    </span>
                    <span className="text-3xl font-black text-indigo-400 tracking-tight font-mono">
                      {fed.display}
                    </span>
                  </div>
                  {indicators?.fedFunds?.value !== undefined && (
                    <div className="text-right">
                      <span className="text-[9px] text-white/40 block">{lang === 'ko' ? '실효금리(DFF)' : 'Effective (DFF)'}</span>
                      <span className="text-xs font-mono font-bold text-white/70">
                        {indicators.fedFunds.value.toFixed(2)}%
                      </span>
                    </div>
                  )}
                </div>

                <div className="mt-3 pt-2.5 border-t border-white/5 grid grid-cols-2 gap-2 text-[10px] text-white/60 relative z-10">
                  <div className="flex items-center gap-1.5">
                    <Calendar className="w-3.5 h-3.5 text-indigo-400" />
                    <span>{lang === 'ko' ? '의결일' : 'Decision'}: <strong className="text-white/90">{fed.decisionDate}</strong></span>
                  </div>
                  <div className="flex items-center gap-1.5">
                    <Calendar className="w-3.5 h-3.5 text-purple-400" />
                    <span>{lang === 'ko' ? '시행일' : 'Effective'}: <strong className="text-white/90">{fed.effectiveDate}</strong></span>
                  </div>
                </div>

                {fed.statementSummary && (
                  <div className="mt-3 p-2.5 rounded-xl bg-black/30 border border-white/5 text-[11px] text-indigo-200/90 leading-relaxed relative z-10">
                    {fed.statementSummary}
                  </div>
                )}
              </div>

              {/* 한국은행 카드 */}
              <div className="bg-gradient-to-br from-emerald-500/10 via-slate-800/60 to-slate-900 border border-emerald-500/30 rounded-2xl p-5 relative overflow-hidden">
                <div className="absolute top-0 right-0 w-32 h-32 bg-emerald-500/5 rounded-full blur-2xl pointer-events-none" />
                <div className="flex items-start justify-between relative z-10">
                  <div className="flex items-center gap-2.5">
                    <span className="text-2xl">🇰🇷</span>
                    <div>
                      <h5 className="text-sm font-black text-white">{bok.bankName}</h5>
                      <span className="text-[10px] text-white/40 font-mono">BOK ECOS: 098Y001</span>
                    </div>
                  </div>
                  {renderActionBadge(bok)}
                </div>

                <div className="mt-4 pt-3 border-t border-white/5 flex items-baseline justify-between relative z-10">
                  <div>
                    <span className="text-[10px] text-emerald-300 font-bold uppercase tracking-wider block">
                      {lang === 'ko' ? '한국은행 기준금리 (Base Rate)' : 'BOK Base Rate'}
                    </span>
                    <span className="text-3xl font-black text-emerald-400 tracking-tight font-mono">
                      {bok.display}
                    </span>
                  </div>
                  {bok.nextMeetingDate && (
                    <div className="text-right">
                      <span className="text-[9px] text-white/40 block">{lang === 'ko' ? '차기 금통위' : 'Next Meeting'}</span>
                      <span className="text-xs font-mono font-bold text-white/70">
                        {bok.nextMeetingDate}
                      </span>
                    </div>
                  )}
                </div>

                <div className="mt-3 pt-2.5 border-t border-white/5 grid grid-cols-2 gap-2 text-[10px] text-white/60 relative z-10">
                  <div className="flex items-center gap-1.5">
                    <Calendar className="w-3.5 h-3.5 text-emerald-400" />
                    <span>{lang === 'ko' ? '의결일' : 'Decision'}: <strong className="text-white/90">{bok.decisionDate}</strong></span>
                  </div>
                  <div className="flex items-center gap-1.5">
                    <Calendar className="w-3.5 h-3.5 text-teal-400" />
                    <span>{lang === 'ko' ? '차기일정' : 'Next'}: <strong className="text-white/90">{bok.nextMeetingDate || '-'}</strong></span>
                  </div>
                </div>

                {bok.statementSummary && (
                  <div className="mt-3 p-2.5 rounded-xl bg-black/30 border border-white/5 text-[11px] text-emerald-200/90 leading-relaxed relative z-10">
                    {bok.statementSummary}
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* SECTION 2: 실시간 글로벌 시장 지표 */}
          <div className="space-y-3 pt-2 border-t border-white/10">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2">
              <div className="flex items-center gap-2">
                <Activity className="w-4 h-4 text-cyan-400" />
                <h4 className="text-xs font-black uppercase tracking-wider text-white/80">
                  {lang === 'ko' ? '실시간 주요 시장 지표 (Live Multi-Asset)' : 'Live Market Indicators Grid'}
                </h4>
              </div>

              {/* Category Filter Tabs */}
              <div className="flex items-center gap-1 bg-white/5 p-1 rounded-xl border border-white/10">
                {[
                  { key: 'ALL', label: lang === 'ko' ? '전체' : 'All' },
                  { key: 'INDICES', label: lang === 'ko' ? '지수/변동성' : 'Indices' },
                  { key: 'FX_BONDS', label: lang === 'ko' ? '환율/채권' : 'FX & Bonds' },
                  { key: 'COMMODITIES', label: lang === 'ko' ? '원자재' : 'Commodities' },
                ].map(tab => (
                  <button
                    key={tab.key}
                    onClick={() => setActiveCategory(tab.key as any)}
                    className={`px-3 py-1 text-xs font-bold rounded-lg transition-all cursor-pointer ${
                      activeCategory === tab.key
                        ? 'bg-indigo-600 text-white shadow-lg shadow-indigo-500/20'
                        : 'text-white/50 hover:text-white hover:bg-white/5'
                    }`}
                  >
                    {tab.label}
                  </button>
                ))}
              </div>
            </div>

            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
              {filteredMarketItems.map((item, idx) => {
                const isPositive = (item.chg || 0) >= 0;
                const colorClass = item.isReverse
                  ? (isPositive ? 'text-red-400' : 'text-emerald-400')
                  : (isPositive ? 'text-emerald-400' : 'text-red-400');
                const fractionDigits = item.digits !== undefined ? item.digits : 2;

                return (
                  <div
                    key={idx}
                    className="bg-white/5 border border-white/10 rounded-2xl p-3.5 hover:bg-white/[0.08] hover:border-white/20 transition-all flex flex-col justify-between"
                  >
                    <div>
                      <span className="text-[10px] text-white/40 font-bold uppercase tracking-tight block truncate">
                        {item.label}
                      </span>
                      <div className="text-base font-black font-mono text-white mt-1">
                        {item.val !== undefined && item.val !== null
                          ? `${typeof item.val === 'number' ? item.val.toLocaleString(undefined, { minimumFractionDigits: fractionDigits, maximumFractionDigits: fractionDigits }) : item.val}${item.unit}`
                          : '--'}
                      </div>
                    </div>

                    <div className="mt-2 pt-2 border-t border-white/5 flex items-center justify-between text-[11px] font-bold">
                      {item.chg !== undefined && item.chg !== null ? (
                        <span className={`font-mono ${colorClass}`}>
                          {isPositive ? '▲' : '▼'} {Math.abs(item.chg).toFixed(2)}{item.isScore ? 'p' : '%'}
                        </span>
                      ) : (
                        <span className="text-white/30">-</span>
                      )}
                      <span className="text-[9px] text-white/20 font-mono">LIVE</span>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* SECTION 3: 데이터 수집 및 발표 주기 가이드 */}
          <div className="p-4 rounded-2xl bg-white/[0.02] border border-white/5 text-[11px] text-white/50 space-y-1.5">
            <div className="flex items-center gap-1.5 text-white/70 font-bold">
              <Info className="w-3.5 h-3.5 text-indigo-400" />
              <span>{lang === 'ko' ? '정책금리 및 지표 수집 주기 안내' : 'Data Ingestion & Release Latency Notice'}</span>
            </div>
            <p>
              {lang === 'ko'
                ? '• FOMC 및 한국은행 기준금리는 통화정책 회의 발표 즉시 상한/하한 목표치가 대시보드에 즉시 반영됩니다.'
                : '• FOMC target ranges and BOK base rates are updated immediately upon official monetary policy statements.'}
            </p>
            <p>
              {lang === 'ko'
                ? '• FRED의 실효 연방기금금리(DFF)는 은행 간 실제 콜 거래 가중평균치로 뉴욕 연은에서 익영업일(T+1) 오전에 공식 집계하여 공표합니다.'
                : '• Effective Fed Funds Rate (DFF) reflects actual interbank volume and is published by NY Fed on the next business day (T+1).'}
            </p>
          </div>

        </div>

        {/* Modal Footer */}
        <div className="p-4 border-t border-white/10 bg-white/[0.02] flex items-center justify-between text-xs text-white/40">
          <div className="flex items-center gap-2">
            <span className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse" />
            <span>{lang === 'ko' ? '중앙은행 데이터 피드 연결됨' : 'Central Bank Real-Time Feed Active'}</span>
          </div>
          <button
            onClick={onClose}
            className="px-5 py-2 rounded-xl bg-white text-black font-black text-xs uppercase tracking-wider hover:bg-white/90 transition-all cursor-pointer"
          >
            {lang === 'ko' ? '닫기' : 'Close'}
          </button>
        </div>

      </div>
    </div>
  );
}
